/* eslint-disable react/jsx-props-no-spreading */
import { useResizeObserver } from '@folklore/hooks';
import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import { differenceInSeconds, parseISO } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import useKeyBoardKeys, { KEYS } from '../../hooks/useKeyboard';
import { getImageAtSize } from '../../lib/utils';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import TimerIcon from '../icons/Timer';
import Volume from '../icons/Volume';
import Loading from '../partials/Loading';

import styles from '../../../styles/questions/falling.module.scss';

interface FallingQuestionProps {
    question: FallingQuestion;
    game: FallingGame;
    participation: Participation | null;
    canPlay: boolean;
    speed?: number;
    odds?: number;
    loading?: boolean;
    onAnswer?: ((points: number) => void) | null;
    className?: string | null;
}

function FallingQuestion({
    question,
    game,
    participation,
    canPlay,
    speed = 300,
    odds = 0.4,
    loading = true,
    onAnswer = null,
    className = null,
}: FallingQuestionProps) {
    const {
        choices: initialItems = [],
        duration: gameDuration = 30,
        theme: gameTheme,
    } = game || {};
    const { withQuestionLoading = false } = gameTheme || {};
    const {
        ref,
        entry: { contentRect },
    } = useResizeObserver();

    const { width = null, height = null } = contentRect || {};

    const themeClassName = useThemeClassName(styles);
    const { playerImage = null, backgroundImage = null } = game || {};
    const { rows = 10, cols = 7, winningScore = null } = question || {};

    const { endedAt: participationEndedAt = null } = participation || {};

    const endedAtDate = useMemo(
        () => (participationEndedAt !== null ? parseISO(participationEndedAt) : null),
        [participationEndedAt],
    );
    const timeRemaining = Math.max(
        0,
        endedAtDate !== null ? differenceInSeconds(endedAtDate, new Date()) : gameDuration,
    );
    const started = timeRemaining > 0 && canPlay;

    const { player, background, items } = useMemo(() => {
        const { url: playerImageUrl = null } = getImageAtSize(playerImage, 'medium') || {};
        const { url: backgroundImageUrl = null } =
            getImageAtSize(backgroundImage, 'original') || {};
        return {
            player: playerImageUrl,
            background: backgroundImageUrl,
            items: (initialItems || []).map((choice) => {
                const { image: choiceImage = null } = choice || {};
                const { url: choiceImageUrl = null } = getImageAtSize(choiceImage, 'medium') || {};
                return {
                    ...choice,
                    image: choiceImageUrl || null,
                };
            }),
        };
    }, [playerImage, backgroundImage, initialItems.length]);

    const [seconds, setSeconds] = useState(0);
    const [scale, setScale] = useState(1);
    const [playerCol, setPlayerCol] = useState(Math.floor(cols / 2));

    const pointsRef = useRef(0);
    const itemsRef = useRef<any[]>([]);
    const renderRef = useRef<{ [key: string]: any }>({});

    const pickItem = useCallback(
        (slot: number) => {
            const index = Math.floor(Math.random() * items.length);
            const item = items[index] || {};
            const col = Math.floor(Math.random() * cols);
            return {
                ...item,
                col,
                row: 0,
                slot,
                dead: Math.random() > odds,
            };
        },
        [items, odds],
    );

    useEffect(() => {
        if (!started) {
            return;
        }
        let animationFrame = null;
        let id = setTimeout(() => {
            id = null;
            animationFrame = requestAnimationFrame(() => {
                animationFrame = null;
                const removedItem =
                    itemsRef.current.length === rows ? itemsRef.current.pop() : null;
                const { slot = itemsRef.current.length } = removedItem || {}; // initial fill
                const item = pickItem(parseInt(slot, 10));
                const newItems = [
                    item,
                    ...itemsRef.current.map((it) => ({ ...it, row: it.row + 1 })),
                ];
                itemsRef.current = newItems;
                renderRef.current = newItems.reduce((acc, it) => {
                    acc[it.slot] = it;
                    return acc;
                }, {});
                setSeconds(seconds + 1);
            });
        }, speed);
        return () => {
            if (id !== null) {
                clearTimeout(id);
            }
            if (animationFrame !== null) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [pickItem, seconds, setSeconds, rows, speed, started]);

    useEffect(() => {
        let id: NodeJS.Timeout | null = null;
        if (itemsRef.current.length === rows) {
            const lastIndex = itemsRef.current.length - 1;
            const last = itemsRef.current[lastIndex];
            if (!last.dead && last.col === playerCol) {
                pointsRef.current += last.value || 1;
                last.dead = true;
                setScale(1.2);

                id = setTimeout(() => {
                    setScale(1);
                }, 150);

                // if (pointsRef.current >= winningScore) {
                onAnswer?.(pointsRef.current);
                // }
            }
        }
        return () => {
            if (id) clearTimeout(id);
        };
    }, [cols, playerCol, seconds, setScale, onAnswer, winningScore]);

    const [musicPlaying, setMusicPlaying] = useState(started);
    const playMusic = useCallback(() => {
        setMusicPlaying(!musicPlaying);
    }, [musicPlaying, setMusicPlaying]);
    const { audio = null } = game || {};
    const { url: audioUrl = null } = audio || {};

    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Move player
    const bind = useDrag(
        ({ xy: [x] }) => {
            const { width: currentWidth = 1, left } = ref.current.getBoundingClientRect();
            const ratio = (x - left) / currentWidth;
            if (ratio > 0 && ratio < 1) {
                const newPlayerCol = Math.floor(ratio * cols);
                setPlayerCol(newPlayerCol);
            }
            if (musicPlaying && audioRef.current !== null) {
                try {
                    audioRef.current.play();
                } catch (e) {
                    console.error('e', e);
                }
            }
        },
        {
            axis: 'x',
            filterTaps: true,
        },
    );

    const moveRight = useCallback(() => {
        setPlayerCol((prevIndex) => (prevIndex === cols - 1 ? prevIndex : prevIndex + 1));
    }, [setPlayerCol, cols]);

    const moveLeft = useCallback(() => {
        setPlayerCol((prevIndex) => (prevIndex === 0 ? 0 : prevIndex - 1));
    }, [setPlayerCol]);

    useKeyBoardKeys({
        [KEYS.ARROW_LEFT]: moveLeft,
        [KEYS.ARROW_RIGHT]: moveRight,
    });

    const slots = useMemo(() => Array.from(Array(rows).keys()), [rows]);

    const itemWidth = useMemo(() => width / cols, [width, cols]);
    const itemHeight = useMemo(() => height / rows, [height, rows]);

    useEffect(() => {
        audioRef.current = document.createElement('audio');
        audioRef.current.src = audioUrl;
        audioRef.current.load();
        audioRef.current.playbackRate = 1.0;
        try {
            if (musicPlaying) {
                audioRef.current.play();
            } else {
                audioRef.current.pause();
            }
        } catch (e) {
            console.error(e);
        }
        return () => {
            audioRef.current?.pause();
        };
    }, [audioUrl, musicPlaying]);

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            <div className={styles.inner}>
                <div className={classNames([styles.top, styles.centered])}>
                    <div className={styles.points}>{pointsRef.current} pts</div>
                    <Button className={styles.volume} onClick={playMusic}>
                        <Volume muted={!musicPlaying} />
                    </Button>
                    <div className={styles.timer}>
                        <TimerIcon className={styles.icon} />
                        {timeRemaining}&nbsp;
                        <FormattedMessage defaultMessage="sec" description="Timer label" />
                    </div>
                </div>
                <div
                    ref={ref}
                    className={styles.game}
                    style={{
                        backgroundImage: `url('${background}')`,
                        aspectRatio: `${cols}/${rows}`,
                    }}
                    {...bind()}
                >
                    {slots.map((slot) => {
                        if (typeof renderRef.current[slot] === 'undefined') {
                            return null;
                        }
                        const {
                            col,
                            row: itemRow,
                            image,
                            name,
                            dead,
                        } = renderRef.current[slot] || {};
                        return (
                            <div
                                key={`slot-${slot}`}
                                className={styles.item}
                                style={{
                                    width: `${itemWidth}px`,
                                    height: `${itemHeight}px`,
                                    transform: `translate(${col * itemWidth}px, ${
                                        itemRow * itemHeight
                                    }px)`,
                                    transition:
                                        itemRow !== 0 ? `transform ${speed}ms linear` : null,
                                    opacity:
                                        (itemRow === rows - 1 && playerCol === col) || dead ? 0 : 1,
                                }}
                            >
                                <img className={styles.image} src={image} alt={name} />
                            </div>
                        );
                    })}
                    <div
                        key="player"
                        className={styles.player}
                        style={{
                            width: `${itemWidth}px`,
                            height: `${itemHeight}px`,
                            transform: `translate(${playerCol * itemWidth}px, ${
                                (rows - 1) * itemHeight - 10
                            }px) scale(${scale})`,
                        }}
                    >
                        <img className={styles.playerImage} src={player} alt="player" />
                    </div>
                </div>
                {loading && withQuestionLoading ? (
                    <Loading loading={loading} className={styles.loading} />
                ) : null}
            </div>
        </div>
    );
}

export default FallingQuestion;
